/**
 * MapModal — full-screen dialog wrapping the ProfileMap.
 *
 * Animation: the backdrop fades and the inner panel scales up on open,
 * reverses on close. Implemented with CSS transitions + a `data-state`
 * attribute on the dialog so Tailwind can target both states.
 *
 * Uses a native <dialog> for focus trap + Escape handling.
 * The map is lazy-loaded so Leaflet's bundle is not in the main chunk.
 */
import { X } from 'lucide-react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ProfileMap = lazy(() =>
  import('./profile-map').then((m) => ({ default: m.ProfileMap })),
);

interface MapModalProps {
  open: boolean;
  onClose: () => void;
  gender?: string | null;
  center?: [number, number];
  zoom?: number;
}

export function MapModal({
  open,
  onClose,
  gender,
  center,
  zoom,
}: MapModalProps) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [mapKey, setMapKey] = useState(0);
  // 'closed' | 'opening' | 'open' | 'closing'
  const [phase, setPhase] = useState<'closed' | 'opening' | 'open' | 'closing'>(
    'closed',
  );

  // Drive the open/close lifecycle
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      setMapKey((k) => k + 1);
      dialog.showModal();
      // Next tick: trigger opening animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setPhase('opening'));
      });
      // After transition ends: mark fully open
      const t = setTimeout(() => setPhase('open'), 320);
      return () => clearTimeout(t);
    } else if (phase !== 'closed') {
      setPhase('closing');
      const t = setTimeout(() => {
        dialog.close();
        setPhase('closed');
      }, 280);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) onClose();
  };

  // Escape key
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handler = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handler);
    return () => dialog.removeEventListener('cancel', handler);
  }, [onClose]);

  const isVisible = phase === 'opening' || phase === 'open';

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      // Backdrop: fade via inline style since ::backdrop can't be Tailwind-targeted
      style={{
        background: 'transparent',
        padding: 0,
        margin: 0,
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
        border: 'none',
      }}
      className="backdrop:transition-opacity backdrop:duration-300"
    >
      {/* Animated backdrop tint */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: isVisible ? 1 : 0 }}
      />

      {/* Panel — scale + fade */}
      <div
        ref={panelRef}
        className="absolute inset-4 flex flex-col overflow-hidden rounded-xl bg-background shadow-2xl transition-all duration-300 sm:inset-6"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible
            ? 'scale(1) translateY(0)'
            : 'scale(0.96) translateY(12px)',
        }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold">
            {t('map.title', 'Map view')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('actions.close', 'Close')}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground/70 ring-1 ring-border transition-all hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Map */}
        <div className="relative min-h-0 flex-1">
          {phase !== 'closed' && (
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {t('map.loading', 'Loading map…')}
                </div>
              }
            >
              <ProfileMap
                key={mapKey}
                center={center}
                zoom={zoom}
                gender={gender}
                className="h-full w-full"
              />
            </Suspense>
          )}
        </div>
      </div>
    </dialog>
  );
}
